<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CompleteInvoicesAlt',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    formId: {
      type: String,
      required: false,
      default: function () {
        return uniqueID();
      },
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const labelAlign = ref('right');
  const register_entry_subject_enabled = ref(false);
  const payload = ref({
    print: false,
    print_copies: 1,
    printer: null,
    target: 'remote',
    create_register_entry: false,
    register_entry_subject: null,
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    labelAlign: labelAlign.value,
    register_entry_subject_enabled: register_entry_subject_enabled.value,
    payload: payload.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'labelAlign' },
      {
        name: 'register_entry_subject_enabled',
        cache: {
          type: 'local',
          key: 'IssueInvoices.register_entry_subject_enabled',
        },
      },
      {
        name: 'payload',
        cache: { type: 'local', key: 'IssueInvoices.payload_v2' },
      },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {},
    ux: {},
    dataRefs: {
      labelAlign,
      register_entry_subject_enabled,
      payload,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('CompleteInvoices|Title', 'Complete Invoices'),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.formId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('formId');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_mf0057a74_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_8564662037462133_closeComponent_l74q8kll6_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        cancel: true,
      },
    };
  };
  const act_4186377094414664_closeComponent_xll31lpzq_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
        ok: true,
        value: payload.value,
      },
    };
  };

  const act_7315092382398562_closeModal_mf0057a74 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_mf0057a74_getActionArgs,
    });
  const inst_evh_7315092382398562_close_sco9zqkhr =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_mf0057a74,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_sco9zqkhr],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_lv6s2mptb =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_2l23ogv1p =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_lv6s2mptb,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_2l23ogv1p],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_l74q8kll6 =
    new actionTypes.CloseComponentAction({
      actionArgs: act_8564662037462133_closeComponent_l74q8kll6_getActionArgs,
    });
  const inst_evh_8564662037462133_close_oxuhnhg1j =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_l74q8kll6,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_oxuhnhg1j],
    event: 'close',
  });
  const act_4186377094414664_closeComponent_xll31lpzq =
    new actionTypes.CloseComponentAction({
      actionArgs: act_4186377094414664_closeComponent_xll31lpzq_getActionArgs,
      displayName: 'submit',
    });
  const inst_evh_4186377094414664_submit_p6iglb2yu =
    new actionTypes.EventHandlerImpl({
      action: act_4186377094414664_closeComponent_xll31lpzq,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_4186377094414664_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4186377094414664_submit_p6iglb2yu],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_rdpousyrd =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_4liiu2eri =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_rdpousyrd,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_4liiu2eri],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_sco9zqkhr,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_2l23ogv1p,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_oxuhnhg1j,
    inst_evh_8564662037462133_close,
    inst_evh_4186377094414664_submit_p6iglb2yu,
    submit: inst_evh_4186377094414664_submit_p6iglb2yu,
    inst_evh_4186377094414664_submit,
    inst_evh_2434514588044777_clickPreventStop_4liiu2eri,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_sco9zqkhr = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_sco9zqkhr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_2l23ogv1p = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_2l23ogv1p.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_oxuhnhg1j = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_oxuhnhg1j.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4186377094414664_submit_p6iglb2yu = async (event, scope) => {
    return await actions.inst_evh_4186377094414664_submit_p6iglb2yu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4186377094414664_submit = async (event, scope) => {
    return await actions.inst_evh_4186377094414664_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_4liiu2eri = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_4liiu2eri.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CompleteInvoicesAlt"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpAlert variant="info" show>
        <p class="mb-0 d-flex align-items-center gap-2">
          <ItpIcon fa="info-circle"> </ItpIcon>
          <ItpText
            wrap
            :text="
              pgettext(
                'CompleteInvoices|Submit',
                'It cannot be modified after synchronization.'
              )
            "
          >
          </ItpText>
        </p>
      </ItpAlert>
      <ItpForm
        :id="formId"
        name="confirmSyncInvoice"
        ref="mainForm"
        @submit="evh_4186377094414664_submit($event, {})"
      >
        <ItpFormGroup
          name="create_register_entry"
          :label="pgettext('InvoiceSync', 'Filing')"
          :helpTooltip="
            pgettext(
              'InvoiceSync',
              'Create a register entry after issuing the invoice.'
            )
          "
          :labelAlign="labelAlign"
          :label-cols-md="4"
          :content-cols-md="8"
        >
          <ItpFormCheckbox
            name="print"
            v-model="payload.create_register_entry"
            :value="payload.create_register_entry"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (payload.create_register_entry =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            required
            class="pt-1"
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
        <ItpFormGroup
          name="register_entry_subject"
          :label="pgettext('InvoiceSync', 'Filing subject')"
          :helpTooltip="
            pgettext(
              'InvoiceSync',
              'Use the given subject for the register entries.'
            )
          "
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          contentClass="input-group"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="register_entry_subject_enabled"
              v-model="register_entry_subject_enabled"
              :value="register_entry_subject_enabled"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (register_entry_subject_enabled =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <ItpFormInput
            name="register_entry_subject"
            v-model="payload.register_entry_subject"
            :value="payload.register_entry_subject"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (payload.register_entry_subject =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :disabled="!register_entry_subject_enabled"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          name="copies"
          :label="pgettext('InvoiceSync', 'Print')"
          :labelAlign="labelAlign"
          :label-cols-md="4"
          :content-cols-md="8"
        >
          <ItpFormCheckbox
            name="copies"
            v-model="payload.print"
            :value="payload.print"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (payload.print =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            required
            size="md"
            class="pt-1"
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
        <ItpFormGroup
          name="print_copies"
          :label="pgettext('InvoiceSync', 'Copies')"
          :labelAlign="labelAlign"
          :label-cols-md="4"
          :content-cols-md="3"
        >
          <ItpFormInput
            type="number"
            name="print_copies"
            v-model="payload.print_copies"
            :value="payload.print_copies"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (payload.print_copies =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            required
            :disabled="!payload.print"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          name="printer"
          :label="pgettext('InvoiceSync', 'Printer')"
          :labelAlign="labelAlign"
          :label-cols-md="4"
          :content-cols-md="8"
        >
          <UcPrinterSelector
            :item="payload"
            path="printer"
            clearable
            :disabled="!payload.print"
          >
          </UcPrinterSelector>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('CompleteInvoices|Submit', 'Complete invoices')"
          :disabled="
            !forms.confirmSyncInvoice || !forms.confirmSyncInvoice.submittable
          "
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Cancel')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
